/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, X, UserCheck, UserX, Edit2, Search, Eye, EyeOff, Boxes } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { utentiAPI } from '../api/utenti';
import { useViewport } from '../hooks/useViewport';

const ACCENT = '#8B5CF6';

export default function Utenti() {
  const [filtroRuolo, setFiltroRuolo] = useState('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [searchFocus, setSearchFocus] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const { isMobile } = useViewport();
  const queryClient = useQueryClient();

  // Debounce: la query parte 350ms dopo l'ultimo tasto, non a ogni battuta
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const { data: utenti = [], isLoading, error } = useQuery({
    queryKey: ['utenti', filtroRuolo, searchDebounced],
    queryFn: () => utentiAPI.list(filtroRuolo || undefined, searchDebounced || undefined),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, attivo }) => utentiAPI.toggleAttivo(id, attivo),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['utenti'] }),
  });

  // Valore predefinito del tecnico: se il cliente vede il suo nome oppure no.
  // Sul singolo intervento si può comunque scavalcare, dalla scheda dell'intervento.
  const visibilitaMutation = useMutation({
    mutationFn: ({ id, esterno }) => utentiAPI.update(id, { tecnico_esterno: esterno }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['utenti'] }),
  });

  return (
    <AdminLayout title="Utenti">
      {/* Toolbar */}
      <div style={styles.toolbar}>
        <div style={{ ...styles.searchAndFilters, ...(isMobile ? { flexDirection: 'column', alignItems: 'stretch', width: '100%' } : {}) }}>
          <div style={{ ...styles.searchBox, ...(isMobile ? { width: '100%' } : {}) }}>
            <Search size={16} style={styles.searchIcon} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setSearchFocus(true)}
              onBlur={() => setSearchFocus(false)}
              placeholder="Cerca nome, email, telefono, azienda..."
              style={{
                ...styles.searchInput,
                ...(searchFocus ? { borderColor: ACCENT, boxShadow: '0 0 0 3px rgba(139,92,246,0.15)' } : {}),
              }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={styles.searchClear} title="Pulisci ricerca">
                <X size={15} />
              </button>
            )}
          </div>
          <div style={styles.filters}>
          {[
            { value: '',        label: 'Tutti' },
            { value: 'cliente', label: 'Clienti' },
            { value: 'tecnico', label: 'Tecnici' },
            { value: 'admin',   label: 'Admin' },
          ].map((f) => (
            <button
              key={f.value}
              onClick={() => setFiltroRuolo(f.value)}
              style={{
                ...styles.filterBtn,
                ...(filtroRuolo === f.value ? { backgroundColor: ACCENT, color: 'white', borderColor: ACCENT } : {}),
              }}
            >
              {f.label}
            </button>
          ))}
          </div>
        </div>
        <button onClick={() => setCreating(true)} style={styles.addBtn}>
          <Plus size={16} /> Nuovo utente
        </button>
      </div>

      {/* Tabella */}
      <div style={styles.tableWrapper}>
        {isLoading && <div style={styles.empty}>Caricamento...</div>}
        {error && <div style={styles.empty}>Errore nel caricamento utenti</div>}
        {!isLoading && !error && utenti.length === 0 && (
          <div style={styles.empty}>Nessun utente trovato.</div>
        )}
        {!isLoading && !error && utenti.length > 0 && (
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>Nome</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Ruolo</th>
                <th style={styles.th}>Telefono</th>
                <th style={styles.th}>Stato</th>
                <th style={styles.th}>Visibile al cliente</th>
                <th style={styles.th}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {utenti.map((u) => (
                <tr key={u.id} style={styles.tr}>
                  <td style={styles.td}>
                    <strong>
                      {u.tipo_cliente === 'azienda' && u.ragione_sociale
                        ? u.ragione_sociale
                        : `${u.nome || ''} ${u.cognome || ''}`.trim() || '—'}
                    </strong>
                  </td>
                  <td style={styles.td}>{u.email}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.roleBadge, ...roleStyles[u.role] }}>
                      {u.role}
                    </span>
                  </td>
                  <td style={styles.td}>{u.telefono || '—'}</td>
                  <td style={styles.td}>
                    {u.attivo ? (
                      <span style={{ ...styles.statusBadge, backgroundColor: '#D1FAE5', color: '#065F46' }}>Attivo</span>
                    ) : (
                      <span style={{ ...styles.statusBadge, backgroundColor: '#FEE2E2', color: '#991B1B' }}>Disattivato</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {u.role !== 'tecnico' ? '—' : (
                      <button
                        onClick={() => visibilitaMutation.mutate({ id: u.id, esterno: !u.tecnico_esterno })}
                        disabled={visibilitaMutation.isPending}
                        style={{
                          ...styles.visibilitaBtn,
                          backgroundColor: u.tecnico_esterno ? '#FEF3C7' : '#D1FAE5',
                          color: u.tecnico_esterno ? '#92400E' : '#065F46',
                        }}
                        title={u.tecnico_esterno
                          ? 'Artigiano esterno: il cliente non vede il suo nome. Clicca per renderlo visibile.'
                          : 'Personale interno: il cliente vede il suo nome. Clicca per nasconderlo.'}
                      >
                        {u.tecnico_esterno ? <><EyeOff size={14} /> Nascosto</> : <><Eye size={14} /> Visibile</>}
                      </button>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={styles.azioni}>
                      <button onClick={() => setEditing(u)} style={styles.actionBtn} title="Modifica">
                        <Edit2 size={16} /> Modifica
                      </button>
                      {/* L'inventario esiste solo per i clienti azienda: sul
                          privato e sul personale il bottone non compare. */}
                      {u.role === 'cliente' && u.tipo_cliente === 'azienda' && (
                        <Link
                          to={`/inventari/${u.id}`}
                          style={{ ...styles.actionBtn, textDecoration: 'none' }}
                          title="Inventario del cliente"
                        >
                          <Boxes size={16} /> Inventario
                        </Link>
                      )}
                      <button
                        onClick={() => toggleMutation.mutate({ id: u.id, attivo: !u.attivo })}
                        disabled={toggleMutation.isPending}
                        style={{
                          ...styles.actionBtn,
                          color: u.attivo ? '#991B1B' : '#065F46',
                        }}
                        title={u.attivo ? 'Disattiva' : 'Attiva'}
                      >
                        {u.attivo ? <UserX size={16} /> : <UserCheck size={16} />}
                        {u.attivo ? ' Disattiva' : ' Attiva'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(creating || editing) && (
        <UtenteFormModal
          utente={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            queryClient.invalidateQueries({ queryKey: ['utenti'] });
          }}
        />
      )}
    </AdminLayout>
  );
}

function UtenteFormModal({ utente, onClose, onSaved }) {
  const { isMobile } = useViewport();
  const isEdit = !!utente;
  const [form, setForm] = useState({
    role: utente?.role || 'cliente',
    tipo_cliente: utente?.tipo_cliente || 'privato',
    email: utente?.email || '', password: '', telefono: utente?.telefono || '',
    // privato
    nome: utente?.nome || '', cognome: utente?.cognome || '', indirizzo: utente?.indirizzo || '',
    citta: utente?.citta || '', cap: utente?.cap || '', codice_fiscale: utente?.codice_fiscale || '',
    // azienda
    ragione_sociale: utente?.ragione_sociale || '', partita_iva: utente?.partita_iva || '',
    codice_univoco: utente?.codice_univoco || '', pec: utente?.pec || '', sede: utente?.sede || '',
    indirizzo_fatturazione: utente?.indirizzo_fatturazione || '', iban: utente?.iban || '',
    // tecnico: interno a listino, oppure artigiano esterno con costo e ricarico
    tecnico_esterno: Boolean(utente?.tecnico_esterno),
    costo_orario: utente?.costo_orario ?? '',
    ricarico_tipo: utente?.ricarico_tipo || 'percentuale',
    ricarico_valore: utente?.ricarico_valore ?? '',
  });
  const [error, setError] = useState('');

  const saveMutation = useMutation({
    mutationFn: (payload) => (isEdit ? utentiAPI.update(utente.id, payload) : utentiAPI.create(payload)),
    onSuccess: onSaved,
    onError: (err) => setError(err.response?.data?.error || 'Errore nel salvataggio'),
  });

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setError('');
  };

  // Anteprima: cosa succede a UN'ORA con questi due numeri. Un ricarico si capisce
  // molto meglio vedendo "30 -> 36" che leggendo "20%".
  const numero = (v) => {
    const t = String(v ?? '').trim().replace(',', '.');
    if (t === '' || !/^\d+(\.\d+)?$/.test(t)) return null;
    return parseFloat(t);
  };
  const costoOra = numero(form.costo_orario);
  const ricarico = numero(form.ricarico_valore);
  let anteprima = null;
  if (form.tecnico_esterno && costoOra !== null) {
    if (ricarico === null) {
      anteprima = `Un'ora costa ${costoOra.toFixed(2)} e, senza ricarico, si vende a ${costoOra.toFixed(2)}.`;
    } else {
      const prezzo = form.ricarico_tipo === 'percentuale'
        ? costoOra * (1 + ricarico / 100)
        : costoOra + ricarico;
      anteprima = `Un'ora costa ${costoOra.toFixed(2)} e si vende a ${prezzo.toFixed(2)} (margine ${(prezzo - costoOra).toFixed(2)}).`;
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    // Da admin nessun campo obbligatorio: password inclusa solo se valorizzata
    // (in modifica, vuota = password invariata).
    const t = (v) => (v || '').trim();
    const payload = { role: form.role, email: t(form.email), telefono: t(form.telefono) };
    if (form.password) payload.password = form.password;
    if (form.role === 'tecnico') {
      payload.nome = t(form.nome);
      payload.cognome = t(form.cognome);
      payload.tecnico_esterno = form.tecnico_esterno;
      if (form.tecnico_esterno) {
        if (form.costo_orario !== '' && costoOra === null) {
          return setError('Il costo orario deve essere un numero.');
        }
        if (form.ricarico_valore !== '' && ricarico === null) {
          return setError('Il ricarico deve essere un numero.');
        }
        payload.costo_orario = costoOra;
        payload.ricarico_tipo = ricarico === null ? null : form.ricarico_tipo;
        payload.ricarico_valore = ricarico;
      } else {
        // Passando da esterno a interno si azzerano: lasciarli darebbe l'idea
        // che valgano ancora qualcosa, e invece sull'interno non si guardano.
        payload.costo_orario = null;
        payload.ricarico_tipo = null;
        payload.ricarico_valore = null;
      }
    }
    if (form.role === 'cliente') {
      payload.tipo_cliente = form.tipo_cliente;
      if (form.tipo_cliente === 'privato') {
        Object.assign(payload, {
          nome: t(form.nome), cognome: t(form.cognome), indirizzo: t(form.indirizzo),
          citta: t(form.citta), cap: t(form.cap), codice_fiscale: t(form.codice_fiscale).toUpperCase(),
        });
      } else {
        Object.assign(payload, {
          ragione_sociale: t(form.ragione_sociale), partita_iva: t(form.partita_iva),
          codice_univoco: t(form.codice_univoco).toUpperCase(), pec: t(form.pec),
          sede: t(form.sede), indirizzo_fatturazione: t(form.indirizzo_fatturazione),
          iban: t(form.iban).replace(/\s/g, '').toUpperCase(),
        });
      }
    } else {
      payload.nome = t(form.nome);
      payload.cognome = t(form.cognome);
    }
    saveMutation.mutate(payload);
  };

  const rowStyle = { ...styles.row, ...(isMobile ? { flexDirection: 'column' } : {}) };

  // Helper campo generico (nessun obbligo se non 'req').
  const campo = (key, label, opts = {}) => (
    <div style={styles.field}>
      <label style={styles.label}>{label}{opts.req ? ' *' : ''}</label>
      <input
        type={opts.type || 'text'}
        value={form[key]}
        onChange={handleChange(key)}
        style={styles.input}
        required={!!opts.req}
        maxLength={opts.maxLength}
        minLength={opts.minLength}
        placeholder={opts.placeholder}
      />
      {opts.hint && <small style={styles.hint}>{opts.hint}</small>}
    </div>
  );

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={{ margin: 0 }}>{isEdit ? 'Modifica utente' : 'Nuovo utente'}</h2>
          <button onClick={onClose} style={styles.closeBtn}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Ruolo *</label>
            <select value={form.role} onChange={handleChange('role')} style={styles.input} required>
              <option value="cliente">Cliente</option>
              <option value="tecnico">Tecnico</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {campo('email', 'Email', { type: 'email' })}
          {campo('password', 'Password', { minLength: 6, hint: isEdit ? 'Lascia vuoto per non modificarla.' : "Opzionale. L'utente potrà impostarla/cambiarla dopo." })}
          {campo('telefono', 'Telefono')}

          {form.role === 'cliente' ? (
            <>
              <div style={styles.field}>
                <label style={styles.label}>Tipo cliente</label>
                <div style={styles.tipoRow}>
                  {[{ v: 'privato', l: 'Privato' }, { v: 'azienda', l: 'Azienda' }].map(({ v, l }) => (
                    <button
                      type="button"
                      key={v}
                      onClick={() => { setForm((f) => ({ ...f, tipo_cliente: v })); setError(''); }}
                      style={{ ...styles.tipoBtn, ...(form.tipo_cliente === v ? styles.tipoBtnActive : {}) }}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {form.tipo_cliente === 'privato' ? (
                <>
                  <div style={rowStyle}>{campo('nome', 'Nome')}{campo('cognome', 'Cognome')}</div>
                  {campo('indirizzo', 'Indirizzo')}
                  <div style={rowStyle}>{campo('citta', 'Città')}{campo('cap', 'CAP', { maxLength: 5 })}</div>
                  {campo('codice_fiscale', 'Codice fiscale', { maxLength: 16 })}
                </>
              ) : (
                <>
                  {campo('ragione_sociale', 'Ragione sociale')}
                  <div style={rowStyle}>{campo('partita_iva', 'Partita IVA', { maxLength: 11 })}{campo('codice_univoco', 'Codice univoco (SDI)', { maxLength: 7 })}</div>
                  {campo('pec', 'PEC', { type: 'email' })}
                  {campo('sede', 'Sede legale')}
                  {campo('indirizzo_fatturazione', 'Indirizzo di fatturazione')}
                  {campo('iban', 'IBAN', { maxLength: 34 })}
                </>
              )}
            </>
          ) : (
            <>
              <div style={rowStyle}>{campo('nome', 'Nome')}{campo('cognome', 'Cognome')}</div>

              {form.role === 'tecnico' && (
                <>
                  <div style={styles.field}>
                    <label style={styles.label}>Tipo di tecnico</label>
                    <div style={styles.tipoRow}>
                      {[{ v: false, l: 'Interno' }, { v: true, l: 'Artigiano esterno' }].map(({ v, l }) => (
                        <button
                          type="button"
                          key={String(v)}
                          onClick={() => { setForm((f) => ({ ...f, tecnico_esterno: v })); setError(''); }}
                          style={{ ...styles.tipoBtn, ...(form.tecnico_esterno === v ? styles.tipoBtnActive : {}) }}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                    <div style={styles.hint}>
                      {form.tecnico_esterno
                        ? "Il cliente non vede il suo nome, e le sue ore si pagano a costo piu' ricarico."
                        : 'Le sue ore si vendono alla tariffa oraria di listino, in Impostazioni.'}
                    </div>
                  </div>

                  {/* Solo per gli esterni: sull'interno questi campi non servono e
                      riempirli darebbe l'idea che cambino qualcosa. */}
                  {form.tecnico_esterno && (
                    <>
                      {campo('costo_orario', 'Costo orario dichiarato (euro, IVA esclusa)', {
                        inputMode: 'decimal',
                        hint: 'Quanto costa a noi. Senza questo numero la sua manodopera non entra nel conto.',
                      })}
                      <div style={styles.field}>
                        <label style={styles.label}>Tipo di ricarico</label>
                        <div style={styles.tipoRow}>
                          {[{ v: 'percentuale', l: 'Percentuale' }, { v: 'euro', l: "Euro all'ora" }].map(({ v, l }) => (
                            <button
                              type="button"
                              key={v}
                              onClick={() => { setForm((f) => ({ ...f, ricarico_tipo: v })); setError(''); }}
                              style={{ ...styles.tipoBtn, ...(form.ricarico_tipo === v ? styles.tipoBtnActive : {}) }}
                            >
                              {l}
                            </button>
                          ))}
                        </div>
                      </div>
                      {campo('ricarico_valore', form.ricarico_tipo === 'percentuale' ? 'Ricarico (%)' : 'Ricarico (euro per ora)', {
                        inputMode: 'decimal',
                        hint: "E' il valore di partenza: sul singolo intervento si puo' cambiare.",
                      })}
                      {anteprima && <div style={styles.anteprima}>{anteprima}</div>}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {error && <div style={styles.errorBox}>{error}</div>}

          <div style={styles.modalActions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>Annulla</button>
            <button type="submit" disabled={saveMutation.isPending} style={styles.submitBtn}>
              {saveMutation.isPending ? 'Salvataggio...' : (isEdit ? 'Salva modifiche' : 'Crea utente')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const roleStyles = {
  admin:   { backgroundColor: '#EDE9FE', color: '#5B21B6' },
  tecnico: { backgroundColor: '#D1FAE5', color: '#065F46' },
  cliente: { backgroundColor: '#DBEAFE', color: '#1E40AF' },
};

const styles = {
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
  searchAndFilters: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', flex: 1, minWidth: 0 },
  searchBox: { position: 'relative', display: 'flex', alignItems: 'center', width: '320px', maxWidth: '100%' },
  searchIcon: { position: 'absolute', left: '0.7rem', color: '#9CA3AF', pointerEvents: 'none' },
  searchInput: { width: '100%', padding: '0.5rem 2.1rem 0.5rem 2.2rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '0.88rem', outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s' },
  searchClear: { position: 'absolute', right: '0.45rem', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.2rem', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', borderRadius: '6px' },
  filters: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  filterBtn: { padding: '0.45rem 0.9rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 500 },
  addBtn: { display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
  tableWrapper: { backgroundColor: 'white', borderRadius: '12px', overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '680px' },
  thead: { backgroundColor: '#F9FAFB' },
  th: { padding: '0.85rem 1rem', textAlign: 'left', fontSize: '0.78rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' },
  tr: { borderTop: '1px solid #F3F4F6' },
  td: { padding: '0.85rem 1rem', fontSize: '0.92rem', color: '#374151' },
  empty: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  roleBadge: { padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600 },
  statusBadge: { padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600 },
  visibilitaBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.3rem 0.6rem', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap' },
  actionBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.35rem 0.7rem', backgroundColor: 'transparent', border: '1px solid #E5E7EB', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' },
  azioni: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' },

  modalOverlay: { position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' },
  modal: { backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflow: 'auto' },
  modalHeader: { padding: '1.25rem 1.5rem', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' },
  form: { padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: 1 },
  row: { display: 'flex', gap: '0.75rem' },
  tipoRow: { display: 'flex', gap: '0.5rem' },
  tipoBtn: { flex: 1, padding: '0.55rem', backgroundColor: 'white', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, color: '#374151' },
  tipoBtnActive: { backgroundColor: ACCENT, borderColor: ACCENT, color: 'white' },
  label: { fontSize: '0.82rem', fontWeight: 600, color: '#374151' },
  input: { padding: '0.55rem 0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', width: '100%' },
  hint: { fontSize: '0.75rem', color: '#6B7280' },
  anteprima: { marginTop: '0.5rem', padding: '0.6rem 0.75rem', backgroundColor: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: '8px', fontSize: '0.82rem', color: '#5B21B6', fontWeight: 600 },
  errorBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.65rem', borderRadius: '8px', fontSize: '0.85rem' },
  modalActions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' },
  cancelBtn: { padding: '0.55rem 1.1rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 },
  submitBtn: { padding: '0.55rem 1.1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
};
